import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
} from "recharts";
import {
  api,
  type AnalyticsPeriod,
  type Product,
  type ProductAnalytics,
} from "../api";
import {
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
  chartColor,
} from "../lib/chart-colors";
import { fmtBucketLabel, fmtMoney, fmtQty } from "../lib/format";
import { useBranch } from "../lib/use-branch";
import {
  ProductMovementsTab,
  type MovementDocRef,
} from "./product-movements-tab";
import {
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { EmptyState } from "./ui/empty-state";
import { Money, Mono, StatusChip } from "./ui/ledger";
import { Skeleton } from "./ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";

const PERIODS: AnalyticsPeriod[] = ["day", "week", "month", "quarter", "year"];

type View = "analitika" | "harakatlar";

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "pos" | "neg";
}) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-muted-foreground">
        {label}
      </div>
      <div className="mt-1">
        <Money tone={tone}>{value}</Money>
      </div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * Tovar kartochkasi — jadvaldagi qatorga bosilganda ochiladi. Ikki ko'rinish:
 *
 * - **Analitika** — davr kesimidagi AGREGAT raqamlar va grafik.
 * - **Harakatlar** — xom ledger: har bir yozuv alohida satr, filtrlanadi va
 *   sahifalanadi, manba hujjatiga o'tish mumkin.
 *
 * Ikkovi ataylab alohida: agregat "qancha sotildi"ga, jurnal esa "aynan
 * qaysi hujjat, qaysi filial, kim" savoliga javob beradi.
 *
 * Tannarx/foyda maydonlari `null` bo'lsa (usta ko'rmaydi) — mos kataklar
 * butunlay chiqmaydi, alohida "xodim ko'rinishi" kodi kerak emas.
 */
export function ProductAnalyticsDialog({
  product,
  onOpenDoc,
}: {
  product: Product;
  /** Jurnaldagi hujjat havolasi — chaqiruvchi mos sahifaga olib boradi. */
  onOpenDoc: (ref: MovementDocRef) => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const { selectedBranchId } = useBranch();
  const [view, setView] = useState<View>("analitika");
  const [period, setPeriod] = useState<AnalyticsPeriod>("month");
  const [data, setData] = useState<ProductAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    api
      .productAnalytics(product.id, period, selectedBranchId)
      .then((a) => alive && setData(a))
      .catch((e) => {
        if (!alive) return;
        setErr((e as { message?: string }).message || t("common:common.error"));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [product.id, period, selectedBranchId, t]);

  const chart = useMemo(
    () =>
      (data?.series ?? []).map((p) => ({
        date: fmtBucketLabel(p.date, data!.bucket),
        qty: Number(p.sold_qty),
        revenue: Number(p.revenue),
      })),
    [data],
  );
  const hasSales = chart.some((p) => p.qty > 0);
  const showCost = data?.tannarx != null;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{product.nomi}</DialogTitle>
        <DialogDescription>
          {[product.artikul, product.kategoriya].filter(Boolean).join(" · ") ||
            t("tovars.analytics.subtitle")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList>
            <TabsTrigger value="analitika">
              {t("tovars.analytics.views.analitika")}
            </TabsTrigger>
            <TabsTrigger value="harakatlar">
              {t("tovars.analytics.views.harakatlar")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {view === "harakatlar" ? (
          <ProductMovementsTab product={product} onOpenDoc={onOpenDoc} />
        ) : (
          <div className="space-y-4">
            <Tabs value={period} onValueChange={(v) => setPeriod(v as AnalyticsPeriod)}>
          <TabsList className="overflow-x-auto">
            {PERIODS.map((p) => (
              <TabsTrigger key={p} value={p}>
                {t(`tovars.analytics.periods.${p}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}

        {loading && !data ? (
          <Skeleton className="h-56" />
        ) : !data ? null : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <Stat
                label={t("tovars.analytics.sold")}
                value={`${fmtQty(data.sold.qty)} ${product.birlik}`}
                sub={fmtMoney(data.sold.value)}
              />
              {data.sold.profit != null && (
                <Stat
                  label={t("tovars.analytics.profit")}
                  value={fmtMoney(data.sold.profit)}
                  tone={Number(data.sold.profit) < 0 ? "neg" : "pos"}
                />
              )}
              <Stat
                label={t("tovars.analytics.avgPrice")}
                value={data.avg_sale_price ? fmtMoney(data.avg_sale_price) : "—"}
                sub={t("tovars.analytics.cardPrice", {
                  value: fmtMoney(data.sotuv_narx),
                })}
              />
              <Stat
                label={t("tovars.analytics.onHand")}
                value={`${fmtQty(data.on_hand)} ${product.birlik}`}
                sub={
                  data.days_of_stock != null
                    ? t("tovars.analytics.daysOfStock", { days: data.days_of_stock })
                    : t("tovars.analytics.minOstatka", {
                        value: fmtQty(data.min_ostatka),
                      })
                }
                tone={Number(data.on_hand) <= 0 ? "neg" : undefined}
              />
              <Stat
                label={t("tovars.analytics.standalone")}
                value={`${fmtQty(data.sold_standalone.qty)} ${product.birlik}`}
                sub={fmtMoney(data.sold_standalone.value)}
              />
              <Stat
                label={t("tovars.analytics.inWorks")}
                value={`${fmtQty(data.sold_in_works.qty)} ${product.birlik}`}
                sub={fmtMoney(data.sold_in_works.value)}
              />
              {/* Xarid summasi TANNARX — tannarx ko'rmaydigan xodimga backend
                  uni bermaydi (qty/value null), shuning uchun katak ham
                  chiqmaydi. */}
              {data.purchased.qty != null && (
                <Stat
                  label={t("tovars.analytics.purchased")}
                  value={`${fmtQty(data.purchased.qty)} ${product.birlik}`}
                  sub={fmtMoney(data.purchased.value)}
                />
              )}
              <Stat
                label={t("tovars.analytics.returned")}
                value={`${fmtQty(data.returned.qty)} ${product.birlik}`}
                sub={fmtMoney(data.returned.value)}
                tone={Number(data.returned.qty) > 0 ? "neg" : undefined}
              />
            </div>

            {showCost && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <span>
                  {t("tovars.analytics.cost")}: <Money>{fmtMoney(data.tannarx)}</Money>
                </span>
                {data.margin_pct != null && (
                  <span>
                    {t("tovars.table.margin")}: <Mono>{data.margin_pct}%</Mono>
                  </span>
                )}
                <StatusChip
                  tone={
                    data.holat === "tugagan"
                      ? "danger"
                      : data.holat === "kam"
                        ? "warn"
                        : "ok"
                  }
                >
                  {t(`tovars.holat.${data.holat}`)}
                </StatusChip>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold mb-2">
                {t("tovars.analytics.chartTitle")}
              </h4>
              {!hasSales ? (
                <EmptyState title={t("tovars.analytics.noSales")} className="py-6" />
              ) : (
                <div className="w-full h-[220px]">
                  <ResponsiveContainer>
                    <ComposedChart data={chart}>
                      <CartesianGrid {...CHART_GRID_PROPS} />
                      <XAxis
                        dataKey="date"
                        fontSize={11}
                        stroke="hsl(var(--muted-foreground))"
                        interval="preserveStartEnd"
                        minTickGap={20}
                      />
                      <YAxis
                        yAxisId="qty"
                        fontSize={11}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis
                        yAxisId="rev"
                        orientation="right"
                        fontSize={11}
                        tickFormatter={(v: number) =>
                          v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)
                        }
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <Tooltip
                        {...CHART_TOOLTIP_PROPS}
                        formatter={(v: number, name: string) =>
                          name === t("tovars.analytics.revenue") ? fmtMoney(v) : v
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId="qty"
                        dataKey="qty"
                        name={t("tovars.analytics.qty")}
                        fill={chartColor("chart-4")}
                        radius={[3, 3, 0, 0]}
                      />
                      <Line
                        yAxisId="rev"
                        type="monotone"
                        dataKey="revenue"
                        name={t("tovars.analytics.revenue")}
                        stroke={chartColor("chart-1")}
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {data.branch_levels.length > 1 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  {t("tovars.analytics.branchLevels")}
                </h4>
                <div className="space-y-1">
                  {data.branch_levels.map((b) => (
                    <div
                      key={b.branch_id}
                      className="flex items-center justify-between text-sm border-b last:border-0 py-1.5"
                    >
                      <span className="text-muted-foreground">{b.branch_name}</span>
                      <Money tone={Number(b.miqdor) <= 0 ? "neg" : "default"}>
                        {fmtQty(b.miqdor)} {product.birlik}
                      </Money>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* "Oxirgi harakatlar" ro'yxati bu yerdan olib tashlandi —
                "Harakatlar" tabi uni to'liq qamrab oladi (filtr, sahifalash,
                filial, hujjatga o'tish). Ikki joyda ikki xil kesim bo'lsa
                qaysi biri to'liq ekani noaniq bo'lardi. */}
          </>
        )}
          </div>
        )}
      </DialogBody>
    </>
  );
}
